import { useEffect } from "react";
import { useT } from "@/i18n/LangProvider";
import { SEO_META, SITE_URL } from "@/i18n/translations";
import { langPrefix } from "@/i18n/routing";

/**
 * Dynamic per-route/per-language SEO injector.
 * Updates <title>, meta description, canonical, OG, Twitter, hreflang, JSON-LD.
 *
 * Usage: <SEO page="home" /> or <SEO page="quote" />
 */
export default function SEO({ page = "home" }) {
  const { lang } = useT();

  useEffect(() => {
    const langMeta = SEO_META[lang] || SEO_META.de;
    const pageMeta = langMeta[page] || langMeta.home;

    // Build canonical URL
    const pathByPage = page === "quote" ? "/orcamento" : "/";
    const canonicalUrl = `${SITE_URL}${langPrefix(lang)}${pathByPage === "/" ? "/" : pathByPage}`;

    // --- <title> + <html lang>
    document.title = pageMeta.title;
    document.documentElement.setAttribute("lang", lang);

    // Helpers
    const setMeta = (attr, value, content) => {
      let el = document.head.querySelector(`meta[${attr}="${value}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setLink = (rel, href, extra = {}) => {
      const selectorParts = [`rel="${rel}"`];
      Object.entries(extra).forEach(([k, v]) => selectorParts.push(`${k}="${v}"`));
      const selector = `link[${selectorParts.join("][")}]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    setMeta("name", "description", pageMeta.description);
    setMeta("property", "og:title", pageMeta.title);
    setMeta("property", "og:description", pageMeta.description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:locale", pageMeta.ogLocale);
    setMeta("name", "twitter:title", pageMeta.title);
    setMeta("name", "twitter:description", pageMeta.description);

    // Canonical
    setLink("canonical", canonicalUrl);

    // Alternates
    const alternates = [
      { hreflang: "de", href: `${SITE_URL}${pathByPage === "/" ? "/" : pathByPage}` },
      { hreflang: "de-CH", href: `${SITE_URL}${pathByPage === "/" ? "/" : pathByPage}` },
      { hreflang: "en", href: `${SITE_URL}/en${pathByPage === "/" ? "" : pathByPage}` },
      { hreflang: "pt", href: `${SITE_URL}/pt${pathByPage === "/" ? "" : pathByPage}` },
      { hreflang: "x-default", href: `${SITE_URL}${pathByPage === "/" ? "/" : pathByPage}` },
    ];
    // Remove any existing alternate links first
    document.head
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((el) => el.remove());
    alternates.forEach(({ hreflang, href }) => {
      const el = document.createElement("link");
      el.setAttribute("rel", "alternate");
      el.setAttribute("hreflang", hreflang);
      el.setAttribute("href", href);
      document.head.appendChild(el);
    });
  }, [lang, page]);

  return null;
}
